import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { geocodeFacilities } from '../../utils/geocodeCache';
import { mapLocationsFromProfiles } from '../../utils/userAnalytics';

const MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const MAP_LIBRARIES = ['places'];
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };

function markerRadius(count) {
  return Math.min(6 + Math.sqrt(count) * 3, 28);
}

function UsersMapTab({ profiles }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAP_KEY,
    libraries: MAP_LIBRARIES,
  });

  const locations = useMemo(() => mapLocationsFromProfiles(profiles), [profiles]);
  const [resolved, setResolved] = useState({});
  const [geocoding, setGeocoding] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const needGeocode = useMemo(() => locations.filter(loc => {
    const addr = [loc.city, loc.state, 'USA'].filter(Boolean).join(', ');
    return addr.length > 3;
  }).map(loc => ({
    veId: loc.key,
    city: loc.city,
    state: loc.state,
    _address: [loc.city, loc.state, 'USA'].filter(Boolean).join(', '),
  })), [locations]);

  const needGeocodeKey = useMemo(
    () => needGeocode.map(f => `${f.veId}|${f._address}`).join('||'),
    [needGeocode]
  );

  useEffect(() => {
    if (!isLoaded || !needGeocodeKey) return;
    let cancelled = false;
    setGeocoding(true);
    (async () => {
      const out = await geocodeFacilities(needGeocode);
      if (!cancelled) {
        setResolved(prev => ({ ...prev, ...out }));
        setGeocoding(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isLoaded, needGeocodeKey, needGeocode]);

  const markers = useMemo(() => locations
    .map(loc => {
      const c = resolved[loc.key];
      if (!c) return null;
      return { ...loc, lat: c.lat, lng: c.lng };
    })
    .filter(Boolean), [locations, resolved]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || markers.length === 0 || !window.google?.maps) return;
    if (markers.length === 1) {
      mapRef.current.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
      mapRef.current.setZoom(8);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
    mapRef.current.fitBounds(bounds, 48);
  }, [mapReady, markers]);

  if (!MAP_KEY) {
    return (
      <p className="users-empty">
        Map view requires <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in the environment.
      </p>
    );
  }

  if (loadError) {
    return <p className="users-empty">Google Maps failed to load.</p>;
  }

  const missing = locations.length - markers.length;

  return (
    <div className="users-tab-panel users-map-panel">
      <p className="users-tab-intro">
        Unique users grouped by city/state from login rollup location fields.
        Marker size reflects user count; click for detail.
        {geocoding && <em> Geocoding locations…</em>}
        {missing > 0 && !geocoding && (
          <span className="users-summary-muted"> {missing} location{missing === 1 ? '' : 's'} missing coordinates.</span>
        )}
      </p>

      {!isLoaded ? (
        <p className="users-empty">Loading map…</p>
      ) : markers.length === 0 ? (
        <p className="users-empty">No geocodable user locations in the current scope.</p>
      ) : (
        <div className="users-map-wrap">
          <GoogleMap
            mapContainerClassName="users-map-canvas"
            center={DEFAULT_CENTER}
            zoom={4}
            onLoad={(map) => { mapRef.current = map; setMapReady(true); }}
            options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: true }}
          >
            {markers.map(m => (
              <MarkerF
                key={m.key}
                position={{ lat: m.lat, lng: m.lng }}
                onClick={() => setActiveKey(m.key)}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  fillColor: '#0d9488',
                  fillOpacity: 0.75,
                  strokeColor: '#0f766e',
                  strokeWeight: 1,
                  scale: markerRadius(m.userCount),
                }}
              />
            ))}
            {activeKey && (() => {
              const m = markers.find(x => x.key === activeKey);
              if (!m) return null;
              return (
                <InfoWindow
                  position={{ lat: m.lat, lng: m.lng }}
                  onCloseClick={() => setActiveKey(null)}
                >
                  <div className="users-map-info">
                    <strong>{[m.city, m.state].filter(Boolean).join(', ')}</strong>
                    <div>{m.userCount.toLocaleString()} users</div>
                    <div>{m.logins.toLocaleString()} logins</div>
                  </div>
                </InfoWindow>
              );
            })()}
          </GoogleMap>
        </div>
      )}
    </div>
  );
}

export default UsersMapTab;
